<template>
  <div style="background-color: white; min-width: 60%; padding: 16px;">
    <q-btn icon="close" flat round dense v-close-popup style="float: right;" />
    <h4 style="margin-top: 0;"><q-icon name="grid_view" /> Fichiers</h4>

    <q-banner v-if="success" class="app-banner app-banner--success q-mb-sm" dense rounded>
      Ressource enregistrée
    </q-banner>
    <q-banner v-if="errorBanner" class="app-banner app-banner--error q-mb-sm" dense rounded>
      Erreur durant l'enregistrement
    </q-banner>

    <q-list bordered separator>
      <q-item v-for="row in rows" :key="row.id" class="resource-item">
        <q-item-section avatar>
          <div class="resource-preview">
            <img
              v-if="isImage(row.name) && previews[row.name]"
              :src="previews[row.name]"
              class="preview-thumb"
            />
            <div v-else-if="isImage(row.name)" class="preview-placeholder">
              <q-spinner size="20px" color="grey" />
            </div>
            <div v-else class="preview-placeholder">
              <q-icon
                :name="getFileIcon(row.name)"
                :color="getFileIconColor(row.name)"
                size="28px"
              />
            </div>
          </div>
        </q-item-section>

        <q-item-section>
          <q-item-label>{{ row.title }}</q-item-label>
          <q-item-label caption>{{ row.name }}</q-item-label>
          <q-item-label v-if="row.description" caption class="text-grey-7">{{ row.description }}</q-item-label>
        </q-item-section>

        <q-item-section side>
          <div class="action-btns">
            <q-btn flat round icon="edit" color="primary" size="sm" @click="openModifyDialog(row)">
              <q-tooltip>Modifier</q-tooltip>
            </q-btn>
            <q-btn flat round icon="delete" color="negative" size="sm" @click="deleteItem(row)">
              <q-tooltip>Supprimer</q-tooltip>
            </q-btn>
          </div>
        </q-item-section>
      </q-item>

      <q-item v-if="rows.length === 0">
        <q-item-section class="text-grey text-center">
          Aucun fichier
        </q-item-section>
      </q-item>
    </q-list>

    <q-dialog v-model="modifyDialog" persistent>
      <q-card style="min-width: 450px">
        <q-card-section>
          <div class="text-h6">Modifier le fichier</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-input label="Nom" dense v-model="newName" autofocus />
          <q-input dense label="Description" v-model="newDescription" />
          <q-input dense label="Titre" v-model="newTitle" />
          <q-input dense label="Source" v-model="newSource" />
          <q-input dense label="Url de la source" v-model="newUrlSource" />
        </q-card-section>

        <q-card-actions align="right" class="text-primary">
          <q-btn flat label="Annuler" v-close-popup />
          <q-btn flat label="Modifier" v-close-popup @click="modifyFile" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from "axios";
import { defineEmits } from 'vue';

const emits = defineEmits(['fileUpdated']);
const store = useStore();
const rows = ref([]);
const exhibitId = computed(() => store.state.exhibitId);
const success = ref(false);
const errorBanner = ref(false);
const newName = ref();
const ancientName = ref();
const newTitle = ref();
const newDescription = ref();
const newSource = ref();
const newUrlSource = ref();
const selectedFileId = ref();
const modifyDialog = ref(false);
const previews = reactive({});

const isImage = (fileName) => /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
const isVideo = (fileName) => /\.(mp4|mov|avi|webm|mkv)$/i.test(fileName);
const isAudio = (fileName) => /\.(mp3|wav|ogg|m4a)$/i.test(fileName);
const isPDF = (fileName) => /\.pdf$/i.test(fileName);

const getFileIcon = (fileName) => {
  if (isVideo(fileName)) return 'videocam';
  if (isAudio(fileName)) return 'audiotrack';
  if (isPDF(fileName)) return 'picture_as_pdf';
  return 'insert_drive_file';
};

const getFileIconColor = (fileName) => {
  if (isVideo(fileName)) return 'purple';
  if (isAudio(fileName)) return 'orange';
  if (isPDF(fileName)) return 'red';
  return 'grey';
};

const fetchPreview = async (name) => {
  if (previews[name] || !isImage(name)) return;
  try {
    const url = `${process.env.VUE_APP_BASEURL}/s3Resource/${name}`;
    const response = await axios.get(url);
    previews[name] = response.data;
  } catch (e) {
    console.log('Preview fetch error for', name);
  }
};

const fetchAllPreviews = () => {
  rows.value.forEach(row => {
    if (isImage(row.name)) fetchPreview(row.name);
  });
};

watch(rows, fetchAllPreviews);

const openModifyDialog = (item) => {
  newName.value = item.name;
  ancientName.value = item.name;
  newTitle.value = item.title;
  newDescription.value = item.description;
  newSource.value = item.source;
  newUrlSource.value = item.source_url;
  selectedFileId.value = item.id;
  modifyDialog.value = true;
}

const modifyFile = async () => {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/resourceData`;
    let putBody = {
      "name": newName.value,
      "ancientName": ancientName.value,
      "description": newDescription.value,
      "title": newTitle.value,
      "source": newSource.value,
      "urlSource": newUrlSource.value,
      "resourceId": selectedFileId.value,
    }
    await axios.put(url, putBody);

    success.value = true;
    setTimeout(() => {
      success.value = false;
      modifyDialog.value = false;
      newName.value = "";
      ancientName.value = "";
      newTitle.value = "";
      newDescription.value = "";
      newSource.value = "";
      newUrlSource.value = "";
      selectedFileId.value = "";
      modifyDialog.value = false;
      start();
      emits('fileUpdated');
    }, 4000);
  } catch (error) {
    console.log(error);
    errorBanner.value = true;
    setTimeout(() => {
      errorBanner.value = false;
    }, 4000)
  }
}

const deleteItem = async (item) => {
  if (confirm("Etes-vous sur de vouloir supprimer cette ressource?")) {
    const filtered = rows.value.filter(value => value !== item);
    try {
      const url = `${process.env.VUE_APP_BASEURL}/file`;
      const response = await axios.delete(url, {
        data: {
          name: item.name,
          exhibitId: item.exhibit_id,
          resourceId: item.resource_id
        }
      });
      if (response.status === 200) {
        success.value = true;
        setTimeout(() => {
          success.value = false;
          rows.value = filtered;
          emits("fileUpdated");
        }, 4000);
      }
    } catch (error) {
      console.error("Error: ", error);
      errorBanner.value = true;
      setTimeout(() => {
        errorBanner.value = false;
      }, 4000)
    }
  }
};

const start = async () =>{
  try {
    const url = `${process.env.VUE_APP_BASEURL}/resources`;
    const body = { exhibitId: exhibitId.value };
    const response = await axios.post(url, body);
    rows.value = response.data;
    store.commit('SET_RESOURCESCOUNT', rows.value.length);
  } catch (error) {
    console.error("Error: ", error);
  }
}

onMounted(start);
</script>

<style scoped>
.resource-item {
  padding: 8px 16px;
}

.resource-preview {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.preview-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 6px;
}

.action-btns {
  display: flex;
  gap: 4px;
}
h4 {
    font-size: 20px;
    font-weight: 600;
}
</style>